import { z } from "zod";

// email_verified_at is deliberately three-state, not a boolean:
//   - undefined: the backend this response came from does not expose email
//     verification yet (an old, pre-v0.1.21 deployment) — the feature must be
//     treated as unavailable, not as "unverified".
//   - null: the backend supports the feature and this user has not verified.
//   - string: the backend supports the feature and this user verified at
//     this ISO timestamp.
// This is NOT `.catch(null)`: catching would collapse "field missing because
// the backend is old" into the same value as "field present and genuinely
// null", which would incorrectly show an unverified/resend state — including
// a resend button pointing at an endpoint the old backend doesn't have yet —
// during a staging rollout window where the frontend deploys ahead of the
// backend. `.optional()` preserves the distinction; no Date conversion here,
// matching every other timestamp-on-the-wire field in this package.
export const tenantUserSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  surname: z.string(),
  email: z.string(),
  email_verified_at: z.string().nullable().optional(),
});
export type TenantUser = z.infer<typeof tenantUserSchema>;

export const tenantBusinessSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  slug: z.string(),
  role: z.string(),
});
export type TenantBusiness = z.infer<typeof tenantBusinessSchema>;

export const tenantSubscriptionSchema = z.object({
  status: z.string(),
  trial_ends_at: z.string(),
});
export type TenantSubscription = z.infer<typeof tenantSubscriptionSchema>;

export const tenantLoginResponseSchema = z.object({
  access_token: z.string().min(1),
  token_type: z.string().min(1),
  expires_in: z.number().int().nonnegative(),
  user: tenantUserSchema,
  active_business: tenantBusinessSchema,
  subscription: tenantSubscriptionSchema,
});
export type TenantLoginResponse = z.infer<typeof tenantLoginResponseSchema>;

// The register request body, mirroring viewmodel.RegisterRequest. business_slug
// is optional: omitting it (or sending an empty string) lets the backend
// derive a unique slug from business_name. There is no business_id field —
// register always creates a brand new business owned by the new user.
export interface TenantRegisterInput {
  name: string;
  surname: string;
  email: string;
  phone: string;
  password: string;
  business_name: string;
  business_slug?: string;
}

// Mirrors viewmodel.RegisterResponse. It has the same shape as the login
// response today (both return the same fresh-session envelope), but is kept
// as its own schema so the two request surfaces can diverge without one
// silently changing the other.
export const tenantRegisterResponseSchema = z.object({
  access_token: z.string().min(1),
  token_type: z.string().min(1),
  expires_in: z.number().int().nonnegative(),
  user: tenantUserSchema,
  active_business: tenantBusinessSchema,
  subscription: tenantSubscriptionSchema,
});
export type TenantRegisterResponse = z.infer<
  typeof tenantRegisterResponseSchema
>;

// The change-password request body, mirroring viewmodel.ChangePasswordRequest.
// There is no user id field: the caller's identity comes only from the
// verified bearer token the client already attaches, never from this body.
// The endpoint responds 204 No Content on success, so there is no response
// schema to pair with it.
export interface ChangeTenantPasswordInput {
  current_password: string;
  new_password: string;
}

// The forgot-password request body, mirroring viewmodel.ForgotPasswordRequest.
// Unauthenticated: no bearer token is attached. The endpoint always answers
// 202 with the same fixed, enumeration-safe message regardless of whether the
// email belongs to an account, so there is no response schema to pair with
// it — a caller only needs to know the request was accepted.
export interface ForgotPasswordInput {
  email: string;
}

// The reset-password request body, mirroring viewmodel.ResetPasswordRequest.
// confirm_password is intentionally absent: it is a client-only UX concern,
// never sent to the backend. Unauthenticated: no bearer token is attached,
// and the endpoint responds 204 No Content on success (no new session token),
// so there is no response schema to pair with it.
export interface ResetPasswordInput {
  token: string;
  new_password: string;
}

// The verify-email request body, mirroring viewmodel.VerifyEmailRequest.
// Unauthenticated: no bearer token is attached, and the endpoint responds
// 204 No Content on success, so there is no response schema to pair with it.
// No user id and no email: the backend resolves the target user entirely
// from the token itself.
export interface VerifyTenantEmailInput {
  token: string;
}

export const paginationMetaSchema = z.object({
  total: z.number().int().nonnegative(),
  limit: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
});
export type PaginationMeta = z.infer<typeof paginationMetaSchema>;

export const tenantCategorySchema = z.object({
  id: z.number().int(),
  name: z.string(),
  description: z.string(),
  sort_order: z.number().int(),
  is_active: z.boolean(),
});
export type TenantCategory = z.infer<typeof tenantCategorySchema>;

export const tenantCategoryListSchema = z.array(tenantCategorySchema);

// The create request body, mirroring viewmodel.CategoryCreateRequest. It
// carries no business_id: the active tenant comes from the verified token.
export interface CreateTenantCategoryInput {
  name: string;
  description: string;
  sort_order: number;
  is_active: boolean;
}

// Mirrors viewmodel.MenuItemResponse. Monetary and nutrition values stay
// strings on the wire, exactly as the backend sends them, so no precision is
// lost to a JS number.
export const tenantMenuItemSchema = z.object({
  id: z.number().int(),
  category_id: z.number().int(),
  name: z.string(),
  description: z.string(),
  price: z.string(),
  currency: z.string(),
  calories: z.number().int().nullable(),
  ingredients: z.string(),
  allergens: z.array(z.string()),
  protein: z.string().nullable(),
  carbs: z.string().nullable(),
  fat: z.string().nullable(),
  weight_grams: z.string().nullable(),
  sort_order: z.number().int(),
  is_active: z.boolean(),
  image_url: z.string(),
  image_alt_text: z.string(),
});
export type TenantMenuItem = z.infer<typeof tenantMenuItemSchema>;

export const tenantMenuItemListSchema = z.array(tenantMenuItemSchema);

// The create request body, mirroring viewmodel.MenuItemCreateRequest. price
// stays a string so the backend's own decimal normalization is authoritative,
// and there is no business_id: the active tenant comes from the token.
export interface CreateTenantMenuItemInput {
  category_id: number;
  name: string;
  description: string;
  price: string;
  currency: string;
  calories: number | null;
  ingredients: string;
  allergens: string[];
  protein: string | null;
  carbs: string | null;
  fat: string | null;
  weight_grams: string | null;
  sort_order: number;
  is_active: boolean;
}

// The update request body, mirroring viewmodel.CategoryUpdateRequest. It has
// the same fields as the create body today, but stays a distinct type so the
// two API surfaces can diverge without one silently changing the other.
export interface UpdateTenantCategoryInput {
  name: string;
  description: string;
  sort_order: number;
  is_active: boolean;
}

// The update request body, mirroring viewmodel.MenuItemUpdateRequest. PUT is a
// full replacement, so every field is sent, including the ones the panel's form
// does not expose (ingredients, allergens, nutrition); those are carried back
// unchanged from the current record. It has the same fields as the create body
// today, but stays a distinct type so the two surfaces can diverge without one
// silently changing the other. There is no business_id: the active tenant comes
// from the token.
export interface UpdateTenantMenuItemInput {
  category_id: number;
  name: string;
  description: string;
  price: string;
  currency: string;
  calories: number | null;
  ingredients: string;
  allergens: string[];
  protein: string | null;
  carbs: string | null;
  fat: string | null;
  weight_grams: string | null;
  sort_order: number;
  is_active: boolean;
}

// Mirrors viewmodel.MenuItemImageResponse. Note the field is alt_text here,
// distinct from the menu item's own image_alt_text: this is the upsert
// endpoint's own small response, not a menu item.
export const tenantMenuItemImageSchema = z.object({
  image_url: z.string(),
  alt_text: z.string(),
});
export type TenantMenuItemImage = z.infer<typeof tenantMenuItemImageSchema>;

// The upsert request body, mirroring viewmodel.MenuItemImageUpsertRequest. The
// backend only stores a URL and alt text (no binary upload), never fetches the
// URL, and there is no business_id: the active tenant comes from the token.
export interface UpsertTenantMenuItemImageInput {
  image_url: string;
  alt_text: string;
}

// Mirrors model.AccentPreset/ThemeMode/FontPreset/RadiusPreset — the same
// curated whitelist tokens the Go backend validates on write (4A) and
// normalizes on read (4B). Each is a closed enum, never an arbitrary string.
// Read-only in this package: this slice adds no updateBranding client
// method, no write path — that is Slice 4C.
const brandingAccentPresetSchema = z.enum([
  "terracotta",
  "evergreen",
  "navy",
  "plum",
  "amber",
]);
const brandingThemeModeSchema = z.enum(["system", "light", "dark"]);
const brandingFontPresetSchema = z.enum(["sans", "serif"]);
const brandingRadiusPresetSchema = z.enum(["compact", "default", "rounded"]);

// Safe built-in defaults, mirroring the backend's own fallback values
// (service.normalizeBrandingResponse / the 0005 migration's column
// DEFAULTs).
const defaultBranding = {
  accent_preset: "terracotta",
  theme_mode: "system",
  font_preset: "sans",
  radius_preset: "default",
} as const;

// Mirrors viewmodel.BusinessBrandingResponse (the same shared Go response
// type the public menu contract uses). .catch() defends against a backend
// deployed before this field existed (branding key absent — the whole
// object falls back to defaultBranding) or a single malformed token (that
// field alone falls back); it never silently accepts an arbitrary string —
// .enum() rejects an unrecognized value outright.
export const tenantBusinessBrandingSchema = z
  .object({
    accent_preset: brandingAccentPresetSchema.catch(
      defaultBranding.accent_preset,
    ),
    theme_mode: brandingThemeModeSchema.catch(defaultBranding.theme_mode),
    font_preset: brandingFontPresetSchema.catch(defaultBranding.font_preset),
    radius_preset: brandingRadiusPresetSchema.catch(
      defaultBranding.radius_preset,
    ),
  })
  .catch(defaultBranding);
export type TenantBusinessBranding = z.infer<
  typeof tenantBusinessBrandingSchema
>;

// Mirrors viewmodel.CurrentBusinessResponse. Only the fields the panel reads
// are modelled strictly; theme travels as an opaque object. branding is the
// Slice 4A/4B curated preset contract, unrelated to theme.
export const tenantCurrentBusinessSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  slug: z.string(),
  address: z.string(),
  phone: z.string(),
  logo_url: z.string(),
  location_url: z.string(),
  theme_id: z.number().int().nullable(),
  theme: z.unknown().nullable(),
  branding: tenantBusinessBrandingSchema,
  is_published: z.boolean(),
});
export type TenantCurrentBusiness = z.infer<typeof tenantCurrentBusinessSchema>;

// The publish request body, mirroring viewmodel.PublishBusinessRequest. It
// carries no business_id: the active tenant comes from the verified token.
export interface PublishTenantBusinessInput {
  is_published: boolean;
}

// The branding write request body, mirroring
// viewmodel.UpdateBusinessBrandingRequest (Slice 4A's narrow
// PUT /businesses/current/branding). No business_id: the active tenant
// comes from the verified token, exactly like PublishTenantBusinessInput
// above. Literal unions here (not tenantBusinessBrandingSchema's inferred
// type) match the hand-written-literal convention already used for other
// write inputs, e.g. CreateTenantInvitationInput's `role`.
export interface UpdateTenantBusinessBrandingInput {
  accent_preset: "terracotta" | "evergreen" | "navy" | "plum" | "amber";
  theme_mode: "system" | "light" | "dark";
  font_preset: "sans" | "serif";
  radius_preset: "compact" | "default" | "rounded";
}

// The profile write request body, mirroring
// viewmodel.UpdateBusinessProfileRequest (the narrow
// PUT /businesses/current/profile). No business_id, slug, theme_id,
// is_published or branding field: those stay owned by the full-replacement
// PUT /businesses/current and the branding endpoint respectively, and this
// request has no field to carry them even if a caller tried — the active
// tenant comes from the verified token, exactly like
// UpdateTenantBusinessBrandingInput above. The response reuses
// tenantCurrentBusinessSchema/TenantCurrentBusiness directly: the backend
// returns the same CurrentBusinessResponse UpdateCurrent does, so no new
// response schema is needed.
//
// location_url is required (not optional) in this shape, same as its four
// siblings: an empty string is how "no location link" is represented on the
// wire, matching the backend's own optional-but-always-present string
// contract — there is no separate "absent" state to model with `undefined`.
export interface UpdateTenantBusinessProfileInput {
  name: string;
  address: string;
  phone: string;
  logo_url: string;
  location_url: string;
}

export const tenantBusinessMembershipSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  slug: z.string(),
  role: z.string(),
});
export type TenantBusinessMembership = z.infer<
  typeof tenantBusinessMembershipSchema
>;

export const tenantBusinessMembershipListSchema = z.array(
  tenantBusinessMembershipSchema,
);

export const tenantMeResponseSchema = z.object({
  user: tenantUserSchema,
  active_business: tenantBusinessSchema,
  subscription: tenantSubscriptionSchema,
});
export type TenantMeResponse = z.infer<typeof tenantMeResponseSchema>;

// Team member response, mirroring viewmodel.TeamMemberResponse. No password
// hash — id is the membership row id, not the user id; Slice 5B's
// role-change and remove endpoints are keyed by it.
export const tenantTeamMemberSchema = z.object({
  id: z.number().int(),
  user_id: z.number().int(),
  name: z.string(),
  surname: z.string(),
  email: z.string(),
  role: z.string(),
});
export type TenantTeamMember = z.infer<typeof tenantTeamMemberSchema>;

export const tenantTeamMemberListSchema = z.array(tenantTeamMemberSchema);

// The update-team-member-role request body, mirroring
// viewmodel.UpdateTeamMemberRoleRequest. Owner is never a valid value here —
// the backend rejects it before the request ever reaches the service.
export interface UpdateTenantTeamMemberRoleInput {
  role: "admin" | "editor";
}

// Team invitation response, mirroring viewmodel.TeamInvitationResponse. Never
// carries a token, a token digest or a full invitation URL. status is
// server-derived ("pending" or "expired"), never a stored column.
export const tenantTeamInvitationSchema = z.object({
  id: z.number().int(),
  email: z.string(),
  role: z.string(),
  status: z.string(),
  expires_at: z.string(),
  created_at: z.string(),
});
export type TenantTeamInvitation = z.infer<typeof tenantTeamInvitationSchema>;

export const tenantTeamInvitationListSchema = z.array(tenantTeamInvitationSchema);

// The create-invitation request body, mirroring
// viewmodel.CreateInvitationRequest. There is no business_id: the active
// tenant comes from the verified token, and owner can never be the target
// role.
export interface CreateTenantInvitationInput {
  email: string;
  role: "admin" | "editor";
}

// The public, pre-authentication invitation preview, mirroring
// viewmodel.InspectInvitationResponse. Deliberately has no email field: the
// backend never exposes the invitation's own email through this capability.
export const tenantInvitationInspectSchema = z.object({
  business_name: z.string(),
  role: z.string(),
  account_exists: z.boolean(),
});
export type TenantInvitationInspect = z.infer<
  typeof tenantInvitationInspectSchema
>;

// The public invitation inspect request body, mirroring
// viewmodel.InspectInvitationRequest.
export interface InspectTenantInvitationInput {
  token: string;
}

// The public invitation accept request body, mirroring
// viewmodel.AcceptInvitationRequest. name/surname/password are only required
// when InspectTenantInvitation reported account_exists=false; the invitation
// email itself is never client-supplied. confirm_password is intentionally
// absent — a frontend-only UX concern, never sent to the backend. The
// endpoint responds 204 No Content and never issues a session token, so
// there is no response schema to pair with it.
export interface AcceptTenantInvitationInput {
  token: string;
  name?: string;
  surname?: string;
  password?: string;
}

// Mirrors viewmodel.MenuImportRowErrorResponse.
export const tenantMenuImportRowErrorSchema = z.object({
  field: z.string(),
  code: z.string(),
});

// Mirrors viewmodel.MenuImportPreviewItemResponse. price stays a string, like
// every other monetary field on the wire. Nullable fields mirror the
// backend's *T pointer fields exactly (null, not omitted, when absent).
export const tenantMenuImportPreviewItemSchema = z.object({
  id: z.number().int(),
  row_number: z.number().int(),
  status: z.string(),
  category_name: z.string(),
  existing_category_id: z.number().int().nullable(),
  existing_category_is_active: z.boolean().nullable(),
  will_create_category: z.boolean(),
  name: z.string(),
  description: z.string(),
  price: z.string().nullable(),
  currency: z.string(),
  calories: z.number().int().nullable(),
  ingredients: z.string(),
  allergens: z.array(z.string()),
  protein: z.string().nullable(),
  carbs: z.string().nullable(),
  fat: z.string().nullable(),
  weight_grams: z.string().nullable(),
  sort_order: z.number().int(),
  is_active: z.boolean(),
  errors: z.array(tenantMenuImportRowErrorSchema),
  duplicate: z.boolean(),
});
export type TenantMenuImportPreviewItem = z.infer<
  typeof tenantMenuImportPreviewItemSchema
>;

// Mirrors viewmodel.MenuImportPreviewResponse. The exact same shape is used
// for both CSV and URL preview: source_type ("csv" | "url") is the only
// field that distinguishes them. There is no source URL or filename field —
// the backend never echoes either back.
export const tenantMenuImportPreviewResponseSchema = z.object({
  job_id: z.number().int(),
  source_type: z.string(),
  status: z.string(),
  template_version: z.number().int(),
  delimiter: z.string(),
  decimal_separator: z.string(),
  total_rows: z.number().int(),
  valid_rows: z.number().int(),
  invalid_rows: z.number().int(),
  items: z.array(tenantMenuImportPreviewItemSchema),
});
export type TenantMenuImportPreviewResponse = z.infer<
  typeof tenantMenuImportPreviewResponseSchema
>;

// The CSV preview request is multipart/form-data (a File plus two option
// fields), never JSON — see client.ts's dedicated multipart request path.
export interface PreviewTenantMenuImportCSVInput {
  file: File;
  delimiter: "," | ";";
  decimal_separator: "." | ",";
}

// The URL preview request body, mirroring viewmodel.MenuImportURLPreviewRequest.
export interface PreviewTenantMenuImportURLInput {
  url: string;
}

// The PDF preview request is multipart/form-data (a single File), never JSON —
// see client.ts's dedicated multipart request path. The response reuses the
// exact same MenuImportPreviewResponse shape as CSV and URL (source_type "pdf").
export interface PreviewTenantMenuImportPDFInput {
  file: File;
}

// Mirrors viewmodel.MenuImportConfirmedItemResponse.
export const tenantMenuImportConfirmedItemSchema = z.object({
  id: z.number().int(),
  row_number: z.number().int(),
  menu_item_id: z.number().int(),
  category_id: z.number().int(),
});

// Mirrors viewmodel.MenuImportConfirmResponse. The same confirm response
// shape and endpoint serve both a csv- and a url-sourced job — there is no
// source-specific confirm response.
export const tenantMenuImportConfirmResponseSchema = z.object({
  job_id: z.number().int(),
  status: z.string(),
  confirmed_at: z.string(),
  selected_rows: z.number().int(),
  imported_rows: z.number().int(),
  skipped_rows: z.number().int(),
  invalid_rows: z.number().int(),
  created_category_count: z.number().int(),
  items: z.array(tenantMenuImportConfirmedItemSchema),
});
export type TenantMenuImportConfirmResponse = z.infer<
  typeof tenantMenuImportConfirmResponseSchema
>;

// The confirm request body, mirroring viewmodel.MenuImportConfirmRequest.
export interface ConfirmTenantMenuImportInput {
  item_ids: number[];
}

export interface SuccessEnvelope<T> {
  success: true;
  message?: string;
  data_count: number;
  data: T;
  meta?: PaginationMeta;
}

export interface ErrorEnvelope {
  success: false;
  error_code: string;
  error_message: string;
  details?: Array<{ field?: string; message: string }>;
}
